/**
 * Utilidades para trabajar con dinero sin arrastrar errores de punto flotante
 * (ej. 0.1 + 0.2 !== 0.3 en JS). Toda suma/resta/comparación de montos debe
 * pasar por acá en vez de operar directamente sobre floats -- de lo
 * contrario, dos totales "iguales" calculados por caminos distintos pueden
 * terminar con una diferencia de ~0.01 al compararlos, aunque se vean
 * idénticos una vez formateados para mostrar.
 *
 * Estrategia: toda la aritmética intermedia se hace en unidades menores
 * ENTERAS (Math.round evita el residuo binario) -- centavos para la mayoría
 * de los países (2 decimales), o la precisión que indique `decimals` para
 * países que la necesiten -- y sólo se vuelve a decimal en el borde final
 * (guardar, enviar a BC, mostrar en pantalla).
 */

// Convierte un valor a unidades menores enteras (centavos si decimals=2).
// Es la única unidad segura para sumar/restar/comparar dinero en la app.
export const toCents = (value, decimals = 2) => {
  const safe = Number(value) || 0;
  return Math.round((safe + Number.EPSILON) * 10 ** decimals);
};

// Convierte unidades menores enteras de vuelta a decimal.
export const fromCents = (cents, decimals = 2) => (Number(cents) || 0) / 10 ** decimals;

// Redondea un valor monetario de forma segura (vía unidades menores enteras).
// Reemplaza patrones como `Math.round(value * 100) / 100` o `Number(value.toFixed(2))`.
export const roundMoney = (value, decimals = 2) => fromCents(toCents(value, decimals), decimals);

// Suma una lista de montos (u objetos, con un selector) sin arrastrar error
// de punto flotante. Reemplaza `arr.reduce((sum, x) => sum + x.Amount, 0)`.
export const sumMoney = (values, getValue = (v) => v, decimals = 2) =>
  fromCents(
    (values || []).reduce((sum, item) => sum + toCents(getValue(item), decimals), 0),
    decimals
  );

// a + b de forma segura.
export const addMoney = (a, b, decimals = 2) =>
  fromCents(toCents(a, decimals) + toCents(b, decimals), decimals);

// a - b de forma segura.
export const subtractMoney = (a, b, decimals = 2) =>
  fromCents(toCents(a, decimals) - toCents(b, decimals), decimals);

// Compara dos montos con tolerancia de redondeo (por defecto cero, o sea:
// iguales si al redondear a la precisión indicada dan el mismo valor).
export const moneyEquals = (a, b, tolerance = 0, decimals = 2) =>
  Math.abs(toCents(a, decimals) - toCents(b, decimals)) <= tolerance;

// Decimales para cálculo monetario interno. Deliberadamente NO es
// country.decimals (ese campo de countries.js es solo para formato visual,
// ver formatNumbers.js -- Paraguay ya lo tiene en 0 para mostrar montos sin
// decimales, pero sigue operando internamente en centavos). Costa Rica es el
// único país que opera dinero a más de 2 decimales; todos los demás países
// siguen en centavos como hasta ahora, sin importar su `decimals` de formato.
export const resolveMoneyDecimals = (country) => (country?.code === "CR" ? 5 : 2);
